import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Package, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

function ResetPassword() {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [isValidToken, setIsValidToken] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  useEffect(() => {
    // Logout l'utilisateur si connecté (pour éviter redirection)
    logout('Veuillez reinitialiser votre mot de passe');

    // Extraire le token de l'URL
    const urlParams = new URLSearchParams(location.search);
    const resetToken = urlParams.get('token');
    
    if (resetToken) {
      setToken(resetToken);
      setIsValidToken(true);
    } else {
      setIsValidToken(false);
      toast.error('Token de réinitialisation manquant');
    }
  }, [location, logout]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post('/auth/reset-password', {
        token: token,
        new_password: formData.newPassword
      });
      
      toast.success('Mot de passe réinitialisé avec succès !');
      
      // Rediriger vers la page de login après un délai
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Token invalide ou expiré');
        setIsValidToken(false);
      } else {
        toast.error('Erreur lors de la réinitialisation du mot de passe');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (isValidToken === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100 p-4">
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification du token...</p>
        </div>
      </div>
    );
  }

  if (isValidToken === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100 p-4">
        <div className="w-full max-w-md">
          <Card className="card animate-fadeInUp shadow-2xl border-0">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-semibold text-red-600">Token invalide</CardTitle>
              <CardDescription className="text-gray-500">
                Le lien de réinitialisation est invalide ou a expiré
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-6">
                Veuillez demander un nouveau lien de réinitialisation.
              </p>
              <Button 
                onClick={() => navigate('/')}
                className="w-full btn-modern btn-primary h-12 text-base font-semibold"
              >
                Retour à la connexion
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100 p-4">
      <div className="w-full max-w-md">
        {/* Logo et titre */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-primary p-4 rounded-2xl shadow-lg">
              <Package className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">InventoTrack</h1>
          <p className="text-gray-600">Réinitialisation de mot de passe</p>
        </div>

        <Card className="card animate-fadeInUp shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-semibold text-gray-900 flex items-center justify-center space-x-2">
              <Lock size={24} />
              <span>Nouveau mot de passe</span>
            </CardTitle>
            <CardDescription className="text-gray-500">
              Choisissez un nouveau mot de passe sécurisé
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-sm font-medium text-gray-700">
                  Nouveau mot de passe
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.newPassword}
                    onChange={handleChange}
                    className="form-input pr-12"
                    placeholder="Entrez votre nouveau mot de passe"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  Confirmer le mot de passe
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="form-input pr-12"
                    placeholder="Confirmez votre nouveau mot de passe"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Indicateur de force du mot de passe */}
              {formData.newPassword && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2 text-sm">
                    <CheckCircle 
                      size={16} 
                      className={formData.newPassword.length >= 6 ? 'text-green-500' : 'text-gray-400'} 
                    />
                    <span className={formData.newPassword.length >= 6 ? 'text-green-600' : 'text-gray-500'}>
                      Au moins 6 caractères
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm mt-1">
                    <CheckCircle 
                      size={16} 
                      className={formData.newPassword === formData.confirmPassword && formData.confirmPassword ? 'text-green-500' : 'text-gray-400'} 
                    />
                    <span className={formData.newPassword === formData.confirmPassword && formData.confirmPassword ? 'text-green-600' : 'text-gray-500'}>
                      Mots de passe identiques
                    </span>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || formData.newPassword !== formData.confirmPassword || formData.newPassword.length < 6}
                className="w-full btn-modern btn-primary h-12 text-base font-semibold"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="loading-spinner w-5 h-5 border-white"></div>
                    <span>Réinitialisation...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Lock size={20} />
                    <span>Réinitialiser le mot de passe</span>
                  </div>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-8 text-sm text-gray-500">
          <p>© 2025 InventoTrack. Tous droits réservés.</p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;